import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';
import { updateClientInState } from './clientsSlice';

export const getEntries = createAsyncThunk('entries/getEntries', async (params, { rejectWithValue }) => {
  try {
    const response = await api.get('/entries', { params });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати записи');
  }
});

export const createEntry = createAsyncThunk('entries/createEntry', async (entryData, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.post('/entries', entryData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: response.data.message,
          type: 'success',
        })
      );
    }

    return response.data.entry;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося додати запис');
  }
});

export const updateEntry = createAsyncThunk('entries/updateEntry', async (entryData, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.put('/entries', entryData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: response.data.message,
          type: 'success',
        })
      );
    }

    return response.data.entry;
  } catch (error) {
    return rejectWithValue(
      { message: 'Не вдалося оновити записи', ...error.response?.data } || 'Не вдалося оновити записи'
    );
  }
});

export const updateEntryComment = createAsyncThunk(
  'entries/updateEntryComment',
  async ({ id, comment }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.patch(
        '/entries/comment',
        { id, comment },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data) {
        dispatch(
          addNotification({
            id: Date.now(),
            title: response.data.message,
            type: 'success',
          })
        );
      }

      return response.data.updatedEntry;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося оновити коментар');
    }
  }
);

export const payForEntry = createAsyncThunk(
  'entries/payForEntry',
  async (paymentData, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.patch('/entries/pay', paymentData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        dispatch(
          addNotification({
            id: Date.now(),
            title: response.data.message,
            type: 'success',
          })
        );

        if (response.data.updatedClient) {
          dispatch(updateClientInState(response.data.updatedClient));
        }
      }

      return response.data.payment;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка реєстрації оплати');
    }
  }
);

export const deleteEntry = createAsyncThunk('entries/deleteEntry', async (entryID, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.delete('/entries', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { id: entryID },
    });

    if (response.data) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: response.data.message,
          type: 'success',
        })
      );
    }

    return response.data?.deletedID;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося видалити записи');
  }
});

const entriesSlice = createSlice({
  name: 'entries',
  initialState: {
    entries: [],
    meta: {
      total: 0,
      page: 1,
      limit: 100,
      totalPages: 1,
    },
    isLoading: false,
    error: null,
  },
  reducers: {
    replaceEntries: (state, action) => {
      action.payload.forEach((updatedEntry) => {
        const index = state.entries.findIndex((entry) => entry.id === updatedEntry.id);
        if (index !== -1) {
          state.entries[index] = updatedEntry;
        }
      });
    },
    clearError: (state, action) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEntries.fulfilled, (state, action) => {
        state.entries = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(getEntries.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати записи';
      })
      .addCase(createEntry.fulfilled, (state, action) => {
        const newEntries = [...state.entries, action.payload];

        const sortedEntriesByTime = newEntries.sort((a, b) => {
          const startTimeA = a.startTime.split(':').slice(0, 2).join(':');
          const startTimeB = b.startTime.split(':').slice(0, 2).join(':');
          const startComparison = startTimeA.localeCompare(startTimeB);

          if (startComparison !== 0) {
            return startComparison;
          }

          const endTimeA = a.endTime ? a.endTime.split(':').slice(0, 2).join(':') : null;
          const endTimeB = b.endTime ? b.endTime.split(':').slice(0, 2).join(':') : null;

          if (!endTimeA && !endTimeB) {
            return new Date(a.createdAt) - new Date(b.createdAt);
          }

          if (!endTimeA) return 1;
          if (!endTimeB) return -1;

          const endComparison = endTimeA.localeCompare(endTimeB);
          if (endComparison !== 0) {
            return endComparison;
          }

          return new Date(a.createdAt) - new Date(b.createdAt);
        });

        state.entries = sortedEntriesByTime;
      })
      .addCase(createEntry.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося додати запис';
      })
      .addCase(updateEntry.fulfilled, (state, action) => {
        const index = state.entries.findIndex((entry) => entry.id === action.payload.id);
        if (index !== -1) {
          state.entries[index] = action.payload;
        }
      })
      .addCase(updateEntry.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити запис';
      })
      .addCase(updateEntryComment.fulfilled, (state, action) => {
        const index = state.entries.findIndex((entry) => entry.id === action.payload.id);
        if (index !== -1) {
          state.entries[index].comment = action.payload.comment;
        }
      })
      .addCase(updateEntryComment.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити коментар';
      })
      .addCase(payForEntry.fulfilled, (state, action) => {
        const updatedPayment = action.payload;

        state.entries.forEach((entry) => {
          const participant = entry.participants?.find((p) => p.payment && p.payment.id === updatedPayment.id);
          if (participant) {
            participant.payment = updatedPayment;
          }

          const coach = entry.coaches?.find((c) => c.payment && c.payment.id === updatedPayment.id);
          if (coach) {
            coach.payment = updatedPayment;
          }
        });
      })
      .addCase(payForEntry.rejected, (state, action) => {
        state.error = action.payload || 'Помилка реєстрації оплати';
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.entries = state.entries.filter((entry) => action.payload !== entry.id);
      })
      .addCase(deleteEntry.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося видалити записи';
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const { replaceEntries, clearError } = entriesSlice.actions;

export default entriesSlice.reducer;
